import type { MouseEvent } from "react";
import Button, { type ButtonProps } from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useBusyClick } from "../../hooks/useBusyClick";
import {
  type ConfirmDeleteProp,
  useConfirmableClick,
} from "../../hooks/useConfirmableClick";

type LegacyVariant = ButtonProps["variant"] | "gradient";
type LegacyColor = ButtonProps["color"] | "info" | "dark" | "white" | "light";

export type AppButtonProps = Omit<ButtonProps, "variant" | "color"> & {
  variant?: LegacyVariant;
  color?: LegacyColor;
  /** Controlled busy state (also set automatically while onClick work is in flight). */
  loading?: boolean;
  /** Skip auto-busy for openers / toggles / pure navigation. */
  busyIgnore?: boolean;
  /** Optional label while busy; defaults to keeping children and showing a spinner. */
  busyLabel?: React.ReactNode;
  /** When set, shows a delete confirmation dialog before running onClick. */
  confirmDelete?: ConfirmDeleteProp;
};

/**
 * Win11 rule: never render a transparent button.
 * - gradient → contained
 * - text stays "text" but theme styles it as tonal filled
 * - default → contained
 */
function mapVariant(variant?: LegacyVariant): ButtonProps["variant"] {
  if (variant === "gradient") return "contained";
  if (variant === "text") return "text";
  if (variant === "outlined") return "outlined";
  return "contained";
}

function mapColor(color?: LegacyColor): ButtonProps["color"] {
  if (color === "info") return "primary";
  if (color === "dark" || color === "white" || color === "light") return "inherit";
  return (color as ButtonProps["color"]) ?? "primary";
}

/** Standard MUI Button used across screens (replaces MDButton / raw HTML buttons). */
export default function AppButton({
  variant,
  color,
  size = "small",
  loading = false,
  busyIgnore = false,
  busyLabel,
  confirmDelete,
  disabled,
  onClick,
  children,
  startIcon,
  sx,
  ...rest
}: AppButtonProps) {
  const confirmedOnClick = useConfirmableClick(onClick, confirmDelete);
  const { busy, onClick: busyOnClick } = useBusyClick<MouseEvent<HTMLButtonElement>>(
    confirmedOnClick,
    {
      loading,
      disabled: !!disabled,
      busyIgnore,
    }
  );

  const showBusy = busy && !busyIgnore;
  const spinner = (
    <CircularProgress color="inherit" size={size === "large" ? 18 : 14} />
  );

  return (
    <Button
      variant={mapVariant(variant)}
      color={mapColor(color)}
      size={size}
      disabled={disabled || showBusy}
      onClick={busyOnClick}
      startIcon={showBusy ? spinner : startIcon}
      aria-busy={showBusy || undefined}
      sx={[
        { borderRadius: 1, textTransform: "none", fontWeight: 600 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    >
      {showBusy && busyLabel != null ? busyLabel : children}
    </Button>
  );
}
